import {
  Button,
  cn,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  TextArea,
  TextField,
  toast,
  Typography,
} from "@suraasa/placebo-ui"
import { useQueryClient } from "@tanstack/react-query"
import api from "api"
import { ReviewResult } from "api/resources/review/types"
import { Submission } from "api/resources/submissions/types"
import clsx from "clsx"
import StatusPill from "components/ReviewStatusTag"
import { Edit, Plus, Xmark } from "iconoir-react"
import { useEffect, useRef, useState } from "react"
import { Controller, useFieldArray, useForm } from "react-hook-form"
import { formatDate, handleErrors, pluralize } from "utils/helpers"

type Props = {
  open: boolean
  onClose: () => void
  submissionDetail: Submission
  queryKey: unknown[]
}

type PenaltyField = {
  reason: string
  percentage: string | number
}

const AI_PENALTY = { reason: "AI-Generated Content", percentage: 100 }

const IQARubricSheet = ({
  open,
  onClose,
  submissionDetail,
  queryKey,
}: Props) => {
  const { review, reviewRubrics } = submissionDetail
  const queryClient = useQueryClient()

  const buildInitialRubrics = (): Record<string, string | null> =>
    Object.fromEntries(reviewRubrics.map(r => [r.id, r.marked?.uuid ?? null]))

  const buildInitialPenalties = (): PenaltyField[] =>
    review?.penalties?.map(p => ({
      reason: p.reason,
      percentage: p.percentage,
    })) ?? []

  // saved state ref — updated after each successful save
  const savedState = useRef({
    comment: review?.comments ?? "",
    rubrics: buildInitialRubrics(),
    penalties: buildInitialPenalties(),
  })

  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [comment, setComment] = useState(savedState.current.comment)
  const [selectedRubrics, setSelectedRubrics] = useState<
    Record<string, string | null>
  >(savedState.current.rubrics)

  const { control, register, getValues, watch, reset } = useForm<{
    penalties: PenaltyField[]
  }>({
    defaultValues: { penalties: savedState.current.penalties },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: "penalties",
  })

  const watchedPenalties = watch("penalties")

  // Re-sync local state when submissionDetail prop changes (e.g. on cache update)
  useEffect(() => {
    savedState.current = {
      comment: review?.comments ?? "",
      rubrics: buildInitialRubrics(),
      penalties: buildInitialPenalties(),
    }
    setComment(savedState.current.comment)
    setSelectedRubrics(savedState.current.rubrics)
    reset({ penalties: savedState.current.penalties })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review?.uuid])

  const handleCellClick = (rowId: string, cellUuid: string) => {
    setSelectedRubrics(prev => ({
      ...prev,
      [rowId]: prev[rowId] === cellUuid ? null : cellUuid,
    }))
  }

  const cancelEdit = () => {
    setComment(savedState.current.comment)
    setSelectedRubrics(savedState.current.rubrics)
    reset({ penalties: savedState.current.penalties })
    setIsEditing(false)
    setIsConfirming(false)
  }

  // Dirty flags
  const commentsDirty = comment !== savedState.current.comment
  const rubricsDirty =
    JSON.stringify(selectedRubrics) !==
    JSON.stringify(savedState.current.rubrics)
  const penaltiesDirty =
    JSON.stringify(watchedPenalties) !==
    JSON.stringify(savedState.current.penalties)
  const anyDirty = commentsDirty || rubricsDirty || penaltiesDirty
  const needsRecalc = rubricsDirty || penaltiesDirty

  const activePenalties = () =>
    getValues("penalties").filter(p => p.reason && p.percentage)

  const hasAIPenalty = () =>
    getValues("penalties").some(p => p.reason === AI_PENALTY.reason)

  const doSave = async (recalc: boolean) => {
    if (!review) return
    setIsSaving(true)
    let apiSucceeded = false
    try {
      const payload: Parameters<typeof api.iqa.updateReview>[0]["data"] = {}

      if (commentsDirty || recalc) {
        payload.comments = comment
      }
      if (recalc) {
        payload.penalties = activePenalties().map(p => ({
          reason: p.reason,
          percentage: Number(p.percentage),
        }))
        payload.markedRubricCells = Object.values(selectedRubrics).filter(
          (v): v is string => v !== null
        )
      }

      const updated = await api.iqa.updateReview({
        urlParams: { reviewUuid: review.uuid },
        data: payload,
      })

      // API succeeded — mark it so finally always exits edit mode
      apiSucceeded = true

      // Update saved state
      const newPenalties = (updated.penalties ?? []).map(p => ({
        reason: p.reason,
        percentage: p.percentage,
      }))
      const newRubrics = recalc
        ? { ...selectedRubrics }
        : savedState.current.rubrics
      savedState.current = {
        comment: updated.comments ?? "",
        rubrics: newRubrics,
        penalties: newPenalties,
      }

      // Update React Query cache so header badges refresh
      queryClient.setQueryData(queryKey, (old: Submission) => ({
        ...old,
        review: {
          ...old.review,
          comments: updated.comments,
          marks: updated.marks,
          status: updated.status,
          result: updated.result,
          penalties: updated.penalties,
        },
        reviewRubrics: recalc
          ? old.reviewRubrics.map(r => ({
              ...r,
              marked:
                r.allComments.find(c => c.uuid === selectedRubrics[r.id]) ??
                null,
            }))
          : old.reviewRubrics,
      }))

      reset({ penalties: newPenalties })
      setComment(updated.comments ?? "")
      setSelectedRubrics(savedState.current.rubrics)

      if (recalc) {
        toast.success(
          `Saved. New marks: ${
            updated.marks != null ? Number(updated.marks).toFixed(2) : "—"
          } (${updated.result ?? "unscored"})`
        )
      } else {
        toast.success("Comments saved. Marks unchanged.")
      }
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsSaving(false)
      if (apiSucceeded) {
        setIsEditing(false)
        setIsConfirming(false)
      }
    }
  }

  const onSaveClick = () => {
    if (!anyDirty) return
    if (needsRecalc) {
      setIsConfirming(true)
    } else {
      doSave(false)
    }
  }

  const saveButtonLabel = !anyDirty
    ? "Save"
    : needsRecalc
    ? "Save & Recalculate"
    : "Save Comments"

  return (
    <Sheet
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          if (isEditing) cancelEdit()
          onClose()
        }
      }}
    >
      <SheetContent
        height={90}
        side="right"
        className="w-screen md:w-3/4 md:!max-w-none"
        closeWhenInteractOutside
      >
        <SheetHeader>
          <div className="mr-2 flex w-full flex-wrap items-center justify-between gap-1">
            <div className="flex flex-col gap-0.5">
              {review ? (
                <>
                  <div className="flex flex-col gap-0.5 md:flex-row md:items-center md:gap-1">
                    <Typography>
                      Score:{" "}
                      {review.marks != null
                        ? Number(review.marks).toFixed(2)
                        : "Not yet scored"}
                    </Typography>
                    <div className="hidden h-full w-0.25 bg-onSurface-500 md:block">
                      &nbsp;
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Typography>Status:</Typography>
                      <StatusPill
                        result={review.result as ReviewResult | null}
                        status={review.status}
                        order={undefined}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-0.5 md:flex-row md:items-center md:gap-1">
                    <Typography variant="smallBody" className="text-muted">
                      Reviewed by {review.professor || "—"}
                    </Typography>
                    {review.datePublished && (
                      <>
                        <div className="hidden h-full w-0.25 bg-onSurface-300 md:block">
                          &nbsp;
                        </div>
                        <Typography variant="smallBody" className="text-muted">
                          {formatDate(review.datePublished, "MMM dd, yyyy")}
                        </Typography>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <Typography className="text-onSurface-500">
                  No review exists for this submission yet.
                </Typography>
              )}
            </div>

            {review && !isEditing && (
              <Button
                startAdornment={<Edit />}
                size="sm"
                variant="outlined"
                onClick={() => setIsEditing(true)}
              >
                Edit Review
              </Button>
            )}

            {isEditing && (
              <Button
                startAdornment={<Xmark />}
                size="sm"
                variant="text"
                color="critical"
                onClick={cancelEdit}
              >
                Cancel
              </Button>
            )}
          </div>
        </SheetHeader>

        {!review ? (
          <SheetBody>
            <Typography className="text-onSurface-500">
              IQA edits can only be applied to submissions that have already
              been reviewed.
            </Typography>
          </SheetBody>
        ) : (
          <SheetBody className="flex flex-col gap-3">
            {/* Rubrics */}
            {reviewRubrics.length > 0 ? (
              <div>
                <Typography variant="title3" className="mb-1">
                  Rubrics
                </Typography>
                <div className="flex flex-col gap-3">
                  {reviewRubrics.map(
                    ({ id, criteria, task, allComments }, i) => (
                      <div key={i} className="flex flex-col gap-1">
                        <Typography variant="strong">
                          {[task, criteria].filter(Boolean).join(") ")}
                        </Typography>
                        <div className="grid gap-2 md:grid-cols-3">
                          {allComments
                            .sort((a, b) => a.scale - b.scale)
                            .map(({ comments, scale, uuid }) => {
                              const isMarked = selectedRubrics[id] === uuid
                              const isClickable = isEditing

                              return (
                                <button
                                  key={uuid}
                                  onClick={() => {
                                    if (isClickable) handleCellClick(id, uuid)
                                  }}
                                  disabled={!isClickable}
                                  className={cn(
                                    "group flex text-start rounded-lg flex-grow h-full w-full"
                                  )}
                                >
                                  <div
                                    className={cn(
                                      "h-full flex flex-col grow gap-1 rounded-lg border p-2",
                                      {
                                        "bg-primary-500 text-surface": isMarked,
                                        "hover:border-primary-500 cursor-pointer":
                                          isClickable && !isMarked,
                                        "cursor-default": !isClickable,
                                      }
                                    )}
                                  >
                                    <Typography variant="title4">
                                      {pluralize("Point", scale)}
                                    </Typography>
                                    <Typography
                                      variant="smallBody"
                                      className={cn({
                                        "text-onSurface-800": !isMarked,
                                        "text-surface": isMarked,
                                      })}
                                    >
                                      {comments}
                                    </Typography>
                                  </div>
                                </button>
                              )
                            })}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            ) : (
              <Typography className="text-onSurface-500">
                This assignment has no rubrics — only comments and deductions
                can be edited.
              </Typography>
            )}

            {/* Deductions */}
            <div>
              <Typography variant="title3" className="mb-1">
                Deductions
              </Typography>

              {fields.length === 0 && !isEditing && (
                <Typography className="text-onSurface-500">
                  No deductions applied.
                </Typography>
              )}

              <div className="flex flex-col gap-2">
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-center gap-2">
                    <input
                      disabled={!isEditing}
                      placeholder="Reason"
                      className={clsx(
                        "flex-1 p-0.5 text-sm",
                        isEditing && "rounded border border-onSurface-300"
                      )}
                      {...register(`penalties.${index}.reason`)}
                    />
                    <Controller
                      control={control}
                      name={`penalties.${index}.percentage`}
                      render={({ field: f }) => (
                        <TextField
                          disabled={!isEditing}
                          dense
                          type="number"
                          placeholder="1–100"
                          endIcon="%"
                          className="max-w-16"
                          value={f.value}
                          onChange={f.onChange}
                        />
                      )}
                    />
                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="text-critical-500 hover:text-critical-700"
                        aria-label="Remove deduction"
                      >
                        <Xmark className="size-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {isEditing && (
                <div className="mt-1 flex flex-wrap gap-1">
                  <Button
                    endAdornment={<Plus className="!size-3" />}
                    variant="text"
                    onClick={() => append({ reason: "", percentage: "" })}
                  >
                    Add Deduction
                  </Button>
                  {!hasAIPenalty() && (
                    <Button
                      variant="text"
                      onClick={() =>
                        append({
                          reason: AI_PENALTY.reason,
                          percentage: AI_PENALTY.percentage,
                        })
                      }
                    >
                      Apply AI-Generated Content (100%)
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Comments */}
            <div className="flex flex-col gap-1">
              <Typography variant="title3">Comments</Typography>
              <TextArea
                value={comment}
                onChange={e => {
                  if (isEditing) setComment(e.target.value)
                }}
                readOnly={!isEditing}
                rows={8}
                className={clsx(!isEditing && "bg-onSurface-50")}
              />
            </div>

            {/* Save bar */}
            {isEditing && (
              <div className="flex flex-col gap-1 border-t pt-2">
                {isConfirming ? (
                  <div className="flex flex-col gap-1 rounded-lg border border-warning-300 bg-warning-50 p-2">
                    <Typography variant="strong">
                      Are you sure you want to save and recalculate the score?
                    </Typography>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        loading={isSaving}
                        onClick={() => doSave(true)}
                      >
                        Confirm
                      </Button>
                      <Button
                        size="sm"
                        variant="outlined"
                        onClick={() => setIsConfirming(false)}
                        disabled={isSaving}
                      >
                        Go back
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    disabled={!anyDirty || isSaving}
                    loading={isSaving}
                    onClick={onSaveClick}
                  >
                    {saveButtonLabel}
                  </Button>
                )}
              </div>
            )}
          </SheetBody>
        )}
      </SheetContent>
    </Sheet>
  )
}

export default IQARubricSheet
